import { observer } from 'mobx-react';
import * as React from 'react';
import { Button, FormCell, Grid, GridItem, Header3Text, Icon, IconSize, IconType, TextInput } from 'ui-toolkit';
import { IPolicyJacketUI } from '../classes/PolicyJacketUI';
import { IPolicyOrderDetails } from '../classes/PolicyOrderDetails';
import { PolicyTypeEnum } from '../classes/PolicyTypeEnum';
import { IRecordedDeed } from '../classes/RecordedDeed';
import StringMethods from '../utils/StringMethods';
import DateText from './fields/DateText';
import PriceText from './fields/PriceText';
import PropertyAddress from './fields/PropertyAddress';
import TimeText from './fields/TimeText';
import OpenLinkInNewTab from './LinkInNewTab';
import PageWithOutsideLinks from './PageWithOutsideLinks';
import PremiumPrice from './PremiumPrice';
import TextWithParagraphs from './TextWithParagraphs';
import { AmericanDigitalPreview } from './AmericanDigitalPreview';
import { IRecordedMortgage } from '../classes/RecordedMortgage';
import { ModalTriggerButton } from '../context/components/ModalTriggerButton';
import { eSingleFields } from '../context/state/eSingleFields';
import { eTransactionCategory } from '../../ui-toolkit/enums/eTransactionType';

interface IPolicyScheduleAPreview {
  orderDetails: IPolicyOrderDetails;
  getPolicyJacket: IPolicyJacketUI;
  policyJacketError: any;
  recordedDeed: IRecordedDeed;
  getEndorsements: string[];
  showEndorsements: boolean;
  showMortgageeClause: boolean;
  editAddressUrl: string;
  editAmtOfInsuredURL: string;
  editBorrowerPage: string;
  cdBuilderPage: string;
  showUpdateSpinner: boolean;
  showUpdateSuccess: boolean;
  policyUpdatedSuccess: boolean;
  policyUpdateFetching: boolean;
  pageContainsError: boolean;
  onPolicyNumberBlur: () => void;
  onUpdatePageClick: () => void;
  policyType: PolicyTypeEnum;
  onChangePolicyNumber: (newValue: string | null, error?: string | null) => void;
  isAmericanDigital: boolean;
  recordedMortgage: IRecordedMortgage;
}

interface IState {
  editPolicyNumber: boolean;
  onOutsideClick: boolean;
}

class PolicyScheduleAPreview extends React.Component<IPolicyScheduleAPreview, IState> {
  public readonly state: IState = {
    editPolicyNumber: false,
    onOutsideClick: false,
  };

  public componentWillReceiveProps(newProps: IPolicyScheduleAPreview) {
    if (this.props.showUpdateSpinner && !newProps.showUpdateSpinner && newProps.showUpdateSuccess) {
      this.toggleUpdateWindow();
    }
  }

  public render() {
    return (
      <>
        <Grid>
          <Header3Text>Schedule A</Header3Text>
        </Grid>
        <Grid columnCount={4}>
          <FormCell label={'File number'} wrapInput={false}>
            {this.props.orderDetails.fileNumber || 'None'}
          </FormCell>
          {this.state.editPolicyNumber ? (
            <TextInput
              value={this.props.getPolicyJacket.policyNumber}
              errorText={this.props.policyJacketError.policyNumber || null}
              onChange={this.props.onChangePolicyNumber}
              onBlur={this.onPolicyNumberBlur}
              getInputRef={this.focusOnNumberInput}
              isRequired={true}
              label={'Policy number'}
              placeholder={'0123456789'}
            />
          ) : (
            <GridItem columnStart={2} columnEnd={4}>
              <FormCell
                label={'Policy number'}
                wrapInput={
                  !this.props.policyUpdateFetching && !this.props.policyUpdatedSuccess && !this.props.isAmericanDigital
                }
              >
                <Grid
                  columnCount={this.props.policyUpdatedSuccess ? 3 : this.props.isAmericanDigital ? 1 : 2}
                  width={'min-content'}
                  alignItems={'center'}
                >
                  {this.props.getPolicyJacket.policyNumber || 'None'}
                  {this.props.policyUpdatedSuccess ? <Icon type={IconType.Check} size={IconSize.Small} /> : null}
                  {!this.props.policyUpdateFetching &&
                  !this.props.policyUpdatedSuccess &&
                  !this.props.isAmericanDigital ? (
                    <Button intent="ghost" isInline onClick={this.toggleEditPolicyNumber}>
                      Edit
                    </Button>
                  ) : null}
                </Grid>
              </FormCell>
            </GridItem>
          )}
          <FormCell label={'Loan number'} wrapInput={false}>
            <div>
              <ModalTriggerButton fieldName={eSingleFields.loan_number}>
                {this.props.orderDetails.loanNumber ? this.props.orderDetails.loanNumber : 'None'}
              </ModalTriggerButton>
            </div>
          </FormCell>
          <GridItem columnStart={1} columnEnd={3}>
            <FormCell label={'Property address'} wrapInput={false}>
              <OpenLinkInNewTab link={this.props.editAddressUrl} onClick={this.onOpenNewTabClick}>
                <PropertyAddress address={this.props.orderDetails.propertyAddress} />
              </OpenLinkInNewTab>
            </FormCell>
          </GridItem>
          <FormCell label={'County'} wrapInput={false}>
            <OpenLinkInNewTab link={this.props.editAddressUrl} onClick={this.onOpenNewTabClick}>
              {this.props.orderDetails.propertyAddress === null ? 'None' : this.props.orderDetails.county}
            </OpenLinkInNewTab>
          </FormCell>
          <div />
          <FormCell label={'Amount of Insurance'} wrapInput={false}>
            <OpenLinkInNewTab link={this.props.editAmtOfInsuredURL} onClick={this.onOpenNewTabClick}>
              <PriceText price={this.props.orderDetails.liabilityAmount} />
            </OpenLinkInNewTab>
          </FormCell>
          <FormCell label={'Premium'} wrapInput={false}>
            <OpenLinkInNewTab link={this.props.cdBuilderPage} onClick={this.onOpenNewTabClick}>
              <PremiumPrice premium={this.props.orderDetails.premiums} />
            </OpenLinkInNewTab>
          </FormCell>
          {this.props.isAmericanDigital ? (
            <AmericanDigitalPreview
              policyType={this.props.policyType}
              transactionType={this.props.orderDetails.transactionCategory || eTransactionCategory.Refinance}
              recordedDeed={this.props.recordedDeed}
              recordedMortgage={this.props.recordedMortgage}
            />
          ) : (
            <>
              <FormCell label={'Deed Recorded Date'} wrapInput={false}>
                <DateText date={this.props.recordedDeed.recorded_date} />
              </FormCell>
              <FormCell label={'Deed Recorded Time'} wrapInput={false}>
                <TimeText time={this.props.recordedDeed.recorded_at} />
              </FormCell>
            </>
          )}
          <GridItem columnStart={1} columnEnd={3}>
            <FormCell label={'Name of insured'} wrapInput={false}>
              <OpenLinkInNewTab link={this.props.editBorrowerPage} onClick={this.onOpenNewTabClick}>
                {this.props.orderDetails.nameOfInsured}
              </OpenLinkInNewTab>
            </FormCell>
          </GridItem>
          {this.props.showMortgageeClause ? (
            <GridItem columnStart={3} columnEnd={5}>
              <FormCell label={'Insured clause'}>
                {this.props.orderDetails.mortgageeClause ? this.props.orderDetails.mortgageeClause : 'None'}
              </FormCell>
            </GridItem>
          ) : null}
          <GridItem columnStart={1} columnEnd={5}>
            <FormCell label={'Estate Type'} wrapInput={false}>
              {this.props.recordedDeed.estate_type
                ? StringMethods.snakeCaseToCapitalize(this.props.recordedDeed.estate_type)
                : 'None'}
            </FormCell>
          </GridItem>
          <GridItem columnStart={1} columnEnd={5}>
            <FormCell label={'Title Vested in'} wrapInput={false}>
              <span>{this.props.orderDetails.vestedIn}</span>
            </FormCell>
          </GridItem>
          {this.props.showMortgageeClause ? (
            <>
              <GridItem columnStart={1} columnEnd={5}>
                <FormCell label={'Insured mortgage'} wrapInput={false}>
                  {this.props.orderDetails.insuredMortgage}
                </FormCell>
              </GridItem>
            </>
          ) : null}
          <GridItem columnStart={1} columnEnd={5}>
            <FormCell label={'Legal description'} wrapInput={false}>
              <TextWithParagraphs>{this.props.orderDetails.legalDescription}</TextWithParagraphs>
            </FormCell>
          </GridItem>
          {this.props.showEndorsements ? (
            <GridItem columnStart={1} columnEnd={5}>
              <FormCell label={'Endorsements'} wrapInput={false}>
                {this.props.getEndorsements.map((endorsement) => {
                  return <div key={endorsement}>{endorsement}</div>;
                })}
              </FormCell>
            </GridItem>
          ) : null}
        </Grid>
        <PageWithOutsideLinks
          outsideClick={this.state.onOutsideClick}
          showUpdateSpinner={this.props.showUpdateSpinner}
          showUpdateSuccess={this.props.showUpdateSuccess}
          pageContainsError={this.props.pageContainsError}
          onUpdatePageClick={this.props.onUpdatePageClick}
        />
      </>
    );
  }

  private focusOnNumberInput = (element: HTMLInputElement | null) => {
    if (element) {
      element.focus();
    }
  };

  private onPolicyNumberBlur = () => {
    this.toggleEditPolicyNumber();
    this.props.onPolicyNumberBlur();
  };

  private toggleEditPolicyNumber = () => {
    this.setState((state) => ({
      editPolicyNumber: !state.editPolicyNumber,
    }));
  };

  private onOpenNewTabClick = () => {
    this.toggleUpdateWindow();
  };

  private toggleUpdateWindow = () => {
    this.setState((state) => ({
      onOutsideClick: !state.onOutsideClick,
    }));
  };
}

export default observer(PolicyScheduleAPreview);
